import React, { useState, useEffect } from 'react';
import api from '../../../../api/client';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { Plus } from 'lucide-react';
import { IoArchiveOutline } from "react-icons/io5";
import DynamicBasicDialog from '../../component/DynamicBasicDialog';
import Pagination from '../../component/Pagination';
import NotificationPopup from '../../component/NotificationPopup';
import { usePageLoading } from '../../../../contexts/LoadingContext';
import AddEditCompanyDialog from '../../component/AddEditCompanyDialog';
import PageHeader, { PageCard, PageHeaderButton, PageShell } from '../../../common/PageHeader';


const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

export default function Company({ onLogout }) {
    const [companies, setCompanies] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [showCompanyModel, setShowCompanyModel] = useState(false);
    const [id, setId] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(true);
    usePageLoading(loading, { message: 'Loading companies…' });
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });
    const [mode, setMode] = useState('');
    const [existingCompany, setExistingCompany] = useState(null);

    const handleClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    useEffect(() => {
        fetchCompanies();
    }, []);

    const fetchCompanies = async () => {
        setLoading(true);
        try {
            const res = await api.get('companies/', {
                headers: {
                    'Authorization': `${localStorage.getItem('adminToken')}`,
                },
            });
            const companyData = res.data.results || res.data;
            setCompanies(Array.isArray(companyData) ? companyData : []);
        } catch (err) {
            setCompanies([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteOpen = (id) => {
        setId(id);
        setDialogOpen(true);
    };

    const handleDeleteCompany = async () => {
        try {
            await api.delete(`companies/${id}/`);
            fetchCompanies();
            setDialogOpen(false);
            setSnackbar({ open: true, message: 'Company achived successfully', severity: 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: 'Failed to archive Company. Please try again.', severity: 'error' });
        }
    };

    const openCompanyDetails = (company) => {
        setShowCompanyModel(true);
        setMode("Edit");
        setExistingCompany(company);
    };

    const handleAddCompanyOpen = () => {
        setShowCompanyModel(true);
        setMode("Add");
        setExistingCompany(null);
    };

    const handleAddCompany = async (formData) => {
        try {
            await api.post('companies/', formData);
            setShowCompanyModel(false);
            fetchCompanies();
            setSnackbar({ open: true, message: 'Company added successfully', severity: 'success' });
        } catch (err) {
            const msg = err.response?.data?.error || 'Failed to add company. Please try again.';
            setSnackbar({ open: true, message: msg, severity: 'error' });
        }
    };

    const handleEditCompany = async (formData) => {
        try {
            await api.patch('companies/', formData);
            setShowCompanyModel(false);
            fetchCompanies();
            setSnackbar({ open: true, message: 'Company updated successfully', severity: 'success' });
        } catch (err) {
            const msg = err.response?.data?.error || 'Failed to update company. Please try again.';
            setSnackbar({ open: true, message: msg, severity: 'error' });
        }
    };

    const totalCompanies = companies.length;
    const totalPages = Math.ceil(totalCompanies / pageSize);
    const paginatedCompanies = companies.slice((page - 1) * pageSize, page * pageSize);

    return (
        <PageShell fill={false} className="bg-[#f8fafc]">
            <PageCard overflow={false}>
                <PageHeader
                    title="Companies"
                    subtitle="Manage companies"
                >
                    <PageHeaderButton onClick={handleAddCompanyOpen} icon={<Plus size={14} />}>
                        Add New Company
                    </PageHeaderButton>
                </PageHeader>

                <div className="p-3 sm:p-4">
                    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '12px' }}>
                        <Table sx={{ minWidth: 650 }} aria-label="company table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell align="left">Description</TableCell>
                                    <TableCell align="left">Website</TableCell>
                                    <TableCell align="left">Industry</TableCell>
                                    <TableCell align="left">Created</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? null : paginatedCompanies.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            No record found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginatedCompanies.map((row) => (
                                        <TableRow key={row.id}>
                                            <TableCell>{row.name}</TableCell>
                                            <TableCell align="left">{row.description}</TableCell>
                                            <TableCell align="left">{row.website}</TableCell>
                                            <TableCell align="left">{row.industry}</TableCell>
                                            <TableCell align="left">{formatDate(row.created_at)}</TableCell>
                                            <TableCell align="center">
                                                <Box display="flex" justifyContent="center" gap={1}>
                                                    <IconButton onClick={() => openCompanyDetails(row)}>
                                                        <EditIcon color="primary" />
                                                    </IconButton>
                                                    <IconButton onClick={() => handleDeleteOpen(row.id)}>
                                                        <IoArchiveOutline color="error" />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {totalPages > 1 && (
                        <Pagination
                            currentPage={page}
                            totalPages={totalPages}
                            totalCount={totalCompanies}
                            currentCount={paginatedCompanies.length}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            pageSize={pageSize}
                            showPageSize={true}
                            pageSizeOptions={[10, 25, 50]}
                            hasActiveFilters={false}
                        />
                    )}
                </div>
            </PageCard>

            <AddEditCompanyDialog
                mode={mode}
                existingCompany={existingCompany}
                onSubmit={(formData) => {
                    mode === "Add" ? handleAddCompany(formData) : handleEditCompany(formData);
                }}
                open={showCompanyModel}
                onClose={() => setShowCompanyModel(false)}
            />

            <DynamicBasicDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onConfirm={handleDeleteCompany}
                title="Archive Confirmation"
                content="Are you sure you want to archive this company?"
            />

            <NotificationPopup
                open={snackbar.open}
                onClose={handleClose}
                severity={snackbar.severity}
                message={snackbar.message}
            />
        </PageShell>
    );
}
